import { Component, Injector, Input, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiResponse, BaseModalComponent } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Observable, finalize } from 'rxjs';
import _ from 'lodash';
import { ExpenseConcept } from '../../../../models/expense-concept/expense-concept';
import { ExpenseConceptFormModel, expenseConceptFieldLabels } from '../../../../models/expense-concept/expense-concept-form';
import { ExpenseConceptProxyService } from '../../../../proxies/expense-concept.proxy.service';
import { ControlsOf } from '../../../../utilities/form.types';
import { reportInvalidFormFields } from '../../../../utilities/form-error-reporter.util';

/**
 * Create / edit an expense concept (#5781). The code is assigned by the API (EC-001) and never edited — rules
 * and receipt lines hold it — so it is only shown once it exists. The name follows Item.UI's item master
 * modal: English always, Spanish behind a "Spanish Translate" toggle. Saves through the proxy itself so an API
 * rejection (deactivating a concept an active GL account rule uses) keeps the modal open with the message.
 */
@Component({
  selector: 'app-expense-concept-modal',
  templateUrl: './expense-concept-modal.component.html',
})
export class ExpenseConceptModalComponent extends BaseModalComponent<ExpenseConcept | null> implements OnInit {
  /** Existing concept when editing; null when creating. */
  @Input() concept: ExpenseConcept | null = null;

  private readonly proxy = inject(ExpenseConceptProxyService);
  private readonly translate = inject(TranslateService);

  readonly fieldLabels = expenseConceptFieldLabels;
  readonly isSaving = signal(false);

  readonly form = new FormGroup<ControlsOf<ExpenseConceptFormModel>>({
    // \S: the API trims the name and rejects a blank one, so all-spaces must fail here too (F-15).
    nameEn: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/\S/)] }),
    nameEs: new FormControl('', { nonNullable: true }),
    isActive: new FormControl(true, { nonNullable: true }),
  });

  /** Shows the Spanish name input. UI state only — off means the concept has no Spanish name (it falls back to English). */
  readonly spanishTranslate = new FormControl(false, { nonNullable: true });

  constructor() {
    super(inject(Injector));
  }

  ngOnInit(): void {
    const concept = this.concept;
    if (_.isNil(concept)) {
      return;
    }
    // The API echoes `en` into a missing `es` on read; that copy is not a real translation.
    const hasSpanish = !concept.name.missingTranslations?.includes('es');
    this.form.patchValue(
      { nameEn: concept.name.en, nameEs: hasSpanish ? concept.name.es : '', isActive: concept.isActive },
      { emitEvent: false },
    );
    this.spanishTranslate.setValue(hasSpanish, { emitEvent: false });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return;
    }

    const value = this.form.getRawValue();
    const name = { en: value.nameEn.trim(), es: this.spanishTranslate.value ? value.nameEs.trim() : '' };
    const existing = this.concept;
    const request: Observable<ApiResponse<ExpenseConcept>> = _.isNil(existing)
      ? this.proxy.create({ name, isActive: value.isActive })
      : this.proxy.update(existing.id, { name, isActive: value.isActive });

    this.isSaving.set(true);
    request.pipe(finalize(() => this.isSaving.set(false))).subscribe({
      next: (response) => {
        if (!response.success || _.isNil(response.data)) {
          return;
        }
        GlobalMessageUtil.success(
          this.t('ExpenseConcept_Title', 'Expense Concepts'),
          this.t('ExpenseConcept_Saved', 'Expense concept {code} saved.').replace('{code}', response.data.code),
        );
        this.close({ action: 'save', data: response.data });
      },
      // The error interceptor already shows the API's message; staying open lets the user correct it.
      error: () => undefined,
    });
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }

  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
